import { useTranslation } from 'react-i18next';

import { dashboardLocale } from '../dashboardLocale';
import { FeedingKpiCard } from '../FeedingKpiCard/FeedingKpiCard';
import { systemLabels } from '../systemLabel';

import { cardQuality, formatNumber, headline, type KpiCardProps } from './kpiCardFormat';
import { coverageNote } from './kpiCoverageNote';

export function WeightKpiCard(props: KpiCardProps) {
    const { summary, config } = props;
    const { t, i18n } = useTranslation();
    const locale = dashboardLocale(i18n);
    // IPS 40: name the customer's own system rather than a generic "PMS".
    const system = systemLabels(t, config.pmsSystemName);
    const actualLabel = t('feedingModule.systemActual', { system: system.name });
    const planLabel = t('feedingModule.systemPlan', { system: system.name });
    // The Bioscope mean covers ready pens only and the planning-system means every pen
    // with a value, so each says which pens it rests on.
    const { note, noteReasons } = coverageNote(
        [
            { metric: 'weightBioscope', label: t('feedingModule.bioscope') },
            { metric: 'weightPmsActual', label: actualLabel, namesSystem: true },
            { metric: 'weightPmsPlan', label: planLabel, namesSystem: true },
        ],
        props,
        t
    );

    // On a ready pen the value above is the Kalman trend but the ± is the interval of the
    // day's own estimate, so its explanation names the estimate (IPS 7); unexplained, the ±
    // would claim to describe the trend. Over several pens the ± is the interval of the mean
    // of their estimates rather than any one pen's, so the explanation says that too. It
    // sits in the ± line's tooltip rather than beside it, so the card stays short (U11).
    const interval =
        summary.avgWeightBioscopeConfIntervalG == null
            ? undefined
            : {
                  text: `±${formatNumber(summary.avgWeightBioscopeConfIntervalG, 0, locale, 'g')}`,
                  explanation: [
                      t(
                          summary.coverage.weightBioscope.length > 1
                              ? 'feedingModule.kpi.weightIntervalMeanTip'
                              : 'feedingModule.kpi.weightIntervalTip'
                      ),
                  ],
              };

    return (
        <FeedingKpiCard
            label={t('feedingModule.kpi.avgWeight')}
            quality={cardQuality(summary, summary.avgWeightBioscopeG != null)}
            values={[
                {
                    ...headline(summary.avgWeightBioscopeG, 0, locale, 'g'),
                    // IPS req 11: the interval belongs on this card, under the Bioscope
                    // estimate it qualifies.
                    caption: interval,
                },
            ]}
            details={[
                {
                    label: actualLabel,
                    value: formatNumber(summary.avgWeightPmsActualG, 0, locale, 'g'),
                },
                {
                    label: planLabel,
                    value: formatNumber(summary.avgWeightPmsPlanG, 0, locale, 'g'),
                },
            ]}
            note={note}
            noteReasons={noteReasons}
        />
    );
}
